// ----- Phase 5.X-publish-governance — Modal #2: E-signature re-auth -----
//
// Forces the operator to re-authenticate via MSAL
// `acquireTokenPopup({prompt: 'login'})`. The resulting fresh ID
// token's `iat` + `oid` claims are captured as the signature
// timestamp + signer identity. Backend verifies freshness (within 60s)
// against its own clock.
//
// Why popup (not silent): silent acquisitions return cached tokens
// that may be hours old. Part 11 §11.200 requires the signature to
// be a deliberate, contemporaneous act — caching defeats that.
// `prompt: 'login'` makes Entra walk the user through a full re-auth,
// which produces a token with `iat = now`.
//
// Why popup (not redirect): popup keeps the operator on the Compiler
// page mid-flow; a redirect would lose the entire modal stack and
// require us to rehydrate it post-redirect. Popup also gives us a
// promise we can await for the captured token. msal-browser v5
// dropped `acquireTokenInteractive` from the IPublicClientApplication
// interface in favour of explicit popup/redirect choice; popup is the
// right fit for this in-flow re-auth.
//
// On popup-blocked / `InteractionRequiredAuthError` / network failure:
// we surface a retryable error inline. The modal remains open until
// the operator either re-auths or cancels.

import { useCallback, useState, type ReactElement } from 'react';
import {
  InteractionRequiredAuthError,
  type AuthenticationResult,
} from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { ModalShell } from './modalShell';
import {
  buttonRowStyle,
  primaryButtonStyle,
  secondaryButtonStyle,
} from './modalShellStyles';
import { apiLoginRequest } from '@/lib/auth/msalConfig';
import { isAuthEnabled } from '@/env';
import type { SignatureAttestation } from '@/lib/compiler/publishGovernanceFlow';
import { captureDevAttestation } from '@/lib/compiler/devAttestation';

export interface ESignatureReauthModalProps {
  readonly onSignatureCaptured: (attestation: SignatureAttestation) => void;
  readonly onCancel: () => void;
}

export function ESignatureReauthModal({
  onSignatureCaptured,
  onCancel,
}: ESignatureReauthModalProps): ReactElement {
  const { instance, accounts } = useMsal();
  const account = accounts[0] ?? null;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleReauth = useCallback(async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      if (!isAuthEnabled()) {
        // Dev-bypass path: synthesize an "id token" the dev-mode backend
        // verifier can decode, FOR THE SIGNED-IN USER (T17-D36 — the one
        // builder in lib/compiler/devAttestation.ts). iat = now → always fresh.
        onSignatureCaptured(await captureDevAttestation());
        return;
      }
      if (!account) {
        setError('No active account. Please sign in again before publishing.');
        return;
      }
      const result: AuthenticationResult = await instance.acquireTokenPopup({
        ...apiLoginRequest,
        // `prompt: 'login'` forces Entra to walk through full re-auth
        // (password + any conditional-access MFA) even if a session
        // cookie exists. Cached silent token would defeat the
        // "contemporaneous signature" requirement.
        prompt: 'login',
        account,
      });
      const claims = (result.idTokenClaims ?? {}) as {
        iat?: number;
        oid?: string;
        email?: string;
        preferred_username?: string;
      };
      if (typeof claims.iat !== 'number' || typeof claims.oid !== 'string') {
        setError('Re-authentication succeeded but the token is missing iat/oid claims.');
        return;
      }
      const signerEmail = claims.email ?? claims.preferred_username ?? account.username;
      onSignatureCaptured({
        signerOid: claims.oid,
        signerEmail,
        signatureTimestampUtc: new Date(claims.iat * 1000).toISOString(),
        signatureMeaning: 'approval-for-publication',
        attestationToken: result.idToken,
      });
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        setError('Interactive sign-in was required but not completed. Please try again.');
      } else if (err instanceof Error && err.message.toLowerCase().includes('popup')) {
        setError(
          'Pop-up was blocked. Allow pop-ups for this site, then retry. (Alternatively, your browser may need to permit redirects from Entra.)',
        );
      } else {
        setError(err instanceof Error ? err.message : 'Re-authentication failed.');
      }
    } finally {
      setBusy(false);
    }
  }, [account, instance, onSignatureCaptured]);

  return (
    <ModalShell testId="pgov-esig-modal" title="Confirm your identity to publish">
      <p style={{ margin: 0, fontSize: 13 }}>
        Publishing creates a permanent, regulator-facing record. Re-enter
        your credentials to confirm this is a deliberate act.
      </p>
      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
        Signed in as: <code data-testid="pgov-esig-account">{account?.username ?? '(no account)'}</code>
        <br />
        Server time: <code>{new Date().toISOString()}</code>
      </div>
      {error && (
        <div
          role="alert"
          data-testid="pgov-esig-error"
          style={{
            color: 'var(--danger, #b91c1c)',
            fontSize: 12,
            background: 'rgba(185,28,28,0.05)',
            border: '1px solid rgba(185,28,28,0.2)',
            borderRadius: 4,
            padding: 8,
          }}
        >
          {error}
        </div>
      )}
      <div style={buttonRowStyle}>
        <button
          type="button"
          data-testid="pgov-esig-cancel"
          onClick={onCancel}
          disabled={busy}
          style={secondaryButtonStyle}
        >
          Cancel
        </button>
        <button
          type="button"
          data-testid="pgov-esig-reauth"
          disabled={busy}
          onClick={() => void handleReauth()}
          style={{
            ...primaryButtonStyle,
            opacity: busy ? 0.6 : 1,
            cursor: busy ? 'not-allowed' : 'pointer',
          }}
        >
          {busy ? 'Re-authenticating…' : 'Re-authenticate'}
        </button>
      </div>
    </ModalShell>
  );
}
